import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormProvider } from '@thinknimble/tn-forms-react'
import { VendorForm, TVendorForm, vendorFormLevelValidators } from 'src/services/vendors/forms'
import { useTnForm } from '@thinknimble/tn-forms-react'
import { Modal } from '../modal'
import { Input } from '../input'
import { PhoneNumberInput } from '../phone-number-input'
import { Button } from '../button'
import { ErrorsList } from '../errors'
import { vendorApi } from 'src/services/vendors/api'
import { vendorQueries } from 'src/services/vendors/queries'
import { organizationQueries } from 'src/services/user'
import { SelectWrapper } from '../select-wrapper'
import { CustomSelect } from '../custom-select'
import {
  Vendor,
  vendorTypeLabelMap,
  vendorTypeEnum,
  taxIdTypeLabelMap,
  idNumberTypeLabelMap,
} from 'src/services/vendors'
import { VendorBankAccountsSection } from './vendor-bank-accounts-section'

interface CreateVendorModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (vendor: Vendor) => void
  initialName?: string
}

const vendorTypeOptions = Object.entries(vendorTypeLabelMap).map(([value, label]) => ({
  label,
  value,
}))

const taxIdTypeOptions = Object.entries(taxIdTypeLabelMap).map(([value, label]) => ({
  label,
  value,
}))

const idNumberTypeOptions = Object.entries(idNumberTypeLabelMap).map(([value, label]) => ({
  label,
  value,
}))

const CreateVendorModalContent: React.FC<CreateVendorModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialName = '',
}) => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler } = useTnForm<TVendorForm>()
  const queryClient = useQueryClient()

  const { data: organizations, isLoading: loadingOrgs } = useQuery(organizationQueries.list())

  const organizationOptions =
    organizations?.results?.map((org) => ({
      label: org.name,
      value: org.id,
    })) || []

  // Set initial name when modal opens
  useEffect(() => {
    if (isOpen && initialName) {
      createFormFieldChangeHandler(form._name)(initialName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialName])

  const { mutate: createVendor, isPending } = useMutation({
    mutationFn: (data: any) => vendorApi.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: vendorQueries.all() })
      onSuccess(data)
      onClose()
    },
  })

  const isIndividual = form.vendorType.value === vendorTypeEnum.INDIVIDUAL

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid) return
    createVendor(form.val)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('vendorForm.createTitle')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="rounded-xl border border-border bg-surface p-4">
          <h4 className="mb-3 text-sm font-semibold text-gray-700">{t('vendorForm.basicInfo')}</h4>
          <Input
            label={form._name.label}
            placeholder={form._name.placeholder}
            value={form._name.value}
            onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
            onBlur={() => form._name.validate()}
            autoFocus
          />
          <ErrorsList errors={form._name.errors} />

          <SelectWrapper label={form.vendorType.label} onBlur={() => form.vendorType.validate()}>
            <CustomSelect
              options={vendorTypeOptions}
              values={
                form.vendorType.value
                  ? vendorTypeOptions.filter((o) => o.value === form.vendorType.value)
                  : [vendorTypeOptions.find((o) => o.value === vendorTypeEnum.BUSINESS)!]
              }
              onChange={(values) => {
                createFormFieldChangeHandler(form.vendorType)(values[0].value)
                // Clear organizations when switching to business type
                if (values[0].value === vendorTypeEnum.BUSINESS) {
                  createFormFieldChangeHandler(form.organizations)([])
                }
              }}
            />
          </SelectWrapper>
          <ErrorsList errors={form.vendorType.errors} />

          {isIndividual && (
            <>
              <SelectWrapper
                label={`${form.organizations.label} ${t('vendorForm.requiredForIndividuals')}`}
                hint={t('vendorForm.privacyHint')}
                onBlur={() => form.organizations.validate()}
              >
                <CustomSelect
                  multi
                  searchable
                  options={organizationOptions}
                  values={organizationOptions.filter(
                    (o) => form.organizations.value?.includes(o.value),
                  )}
                  loading={loadingOrgs}
                  onChange={(values) =>
                    createFormFieldChangeHandler(form.organizations)(values.map((v) => v.value))
                  }
                  placeholder={t('vendorForm.selectOrganizations')}
                />
              </SelectWrapper>
              <ErrorsList errors={form.organizations.errors} />
            </>
          )}
        </div>

        {/* Contact Information */}
        <div className="rounded-xl border border-border bg-surface p-4">
          <h4 className="mb-3 text-sm font-semibold text-gray-700">
            {t('vendorForm.contactInfo')}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label={form.email.label}
                placeholder={form.email.placeholder}
                value={form.email.value}
                type="email"
                onChange={(e) => createFormFieldChangeHandler(form.email)(e.target.value)}
                onBlur={() => form.email.validate()}
              />
              <ErrorsList errors={form.email.errors} />
            </div>

            <div>
              <PhoneNumberInput
                label={form.phone.label}
                placeholder={form.phone.placeholder}
                value={form.phone.value || undefined}
                onChange={(value) => createFormFieldChangeHandler(form.phone)(value ?? '')}
                onBlur={() => form.phone.validate()}
              />
              <ErrorsList errors={form.phone.errors} />
            </div>
          </div>
        </div>

        {/* Tax Information */}
        <div className="rounded-xl border border-border bg-surface p-4">
          <h4 className="mb-3 text-sm font-semibold text-gray-700">{t('vendorForm.taxInfo')}</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label={form.taxId.label}
                placeholder={form.taxId.placeholder}
                value={form.taxId.value}
                onChange={(e) => createFormFieldChangeHandler(form.taxId)(e.target.value)}
                onBlur={() => form.taxId.validate()}
              />
              <ErrorsList errors={form.taxId.errors} />
            </div>

            <div>
              <SelectWrapper label={form.taxIdType.label} onBlur={() => form.taxIdType.validate()}>
                <CustomSelect
                  clearable
                  options={taxIdTypeOptions}
                  values={
                    form.taxIdType.value
                      ? taxIdTypeOptions.filter((o) => o.value === form.taxIdType.value)
                      : []
                  }
                  onChange={(values) =>
                    createFormFieldChangeHandler(form.taxIdType)(values[0]?.value || null)
                  }
                />
              </SelectWrapper>
              <ErrorsList errors={form.taxIdType.errors} />
            </div>
          </div>

          {isIndividual && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    label={
                      form.country.value === 'CO'
                        ? t('vendorForm.identificationNumberCo')
                        : t('vendorForm.identificationNumber')
                    }
                    placeholder={form.identificationNumber.placeholder}
                    value={form.identificationNumber.value}
                    onChange={(e) =>
                      createFormFieldChangeHandler(form.identificationNumber)(e.target.value)
                    }
                    onBlur={() => form.identificationNumber.validate()}
                  />
                  <ErrorsList errors={form.identificationNumber.errors} />
                </div>
                <div>
                  <SelectWrapper
                    label={form.identificationNumberType.label}
                    onBlur={() => form.identificationNumberType.validate()}
                  >
                    <CustomSelect
                      clearable
                      options={idNumberTypeOptions}
                      values={
                        form.identificationNumberType.value
                          ? idNumberTypeOptions.filter(
                              (o) => o.value === form.identificationNumberType.value,
                            )
                          : []
                      }
                      onChange={(values) =>
                        createFormFieldChangeHandler(form.identificationNumberType)(
                          values[0]?.value || null,
                        )
                      }
                    />
                  </SelectWrapper>
                  <ErrorsList errors={form.identificationNumberType.errors} />
                </div>
              </div>
              <div>
                <Input
                  label={form.withholdingRate.label}
                  hint={t('vendorForm.withholdingHint')}
                  placeholder={form.withholdingRate.placeholder}
                  value={form.withholdingRate.value}
                  onChange={(e) =>
                    createFormFieldChangeHandler(form.withholdingRate)(e.target.value)
                  }
                  onBlur={() => form.withholdingRate.validate()}
                />
                <ErrorsList errors={form.withholdingRate.errors} />
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="rounded-xl border border-border bg-surface p-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">{form.notes.label}</label>
          <textarea
            className="w-full rounded-md border border-border bg-surface p-2"
            rows={3}
            placeholder={form.notes.placeholder}
            value={form.notes.value}
            onChange={(e) => createFormFieldChangeHandler(form.notes)(e.target.value)}
            onBlur={() => form.notes.validate()}
          />
          <ErrorsList errors={form.notes.errors} />
        </div>

        {/* Bank Accounts */}
        <VendorBankAccountsSection vendorId={null} />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? t('vendorForm.creatingVendor') : t('common.create')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const CreateVendorModal: React.FC<CreateVendorModalProps> = (props) => {
  // Reset form when modal opens/closes by using key
  return (
    <FormProvider
      formClass={VendorForm}
      formLevelValidators={vendorFormLevelValidators}
      key={props.isOpen ? 'open' : 'closed'}
    >
      <CreateVendorModalContent {...props} />
    </FormProvider>
  )
}
